"use client";

import { useState, useCallback } from "react";
import { Loader2, PenTool, CheckCircle, ExternalLink } from "lucide-react";
import type { SignatureStatus } from "@/lib/deals/signature";
interface SignButtonProps {
  dealId: string;
  status: SignatureStatus | null;
  onSign: () => Promise<string | void>; // Returns signing URL or void
  disabled?: boolean;
  className?: string;
}

/**
 * SignButton - Button to initiate signing
 */
export function SignButton({
  status,
  onSign,
  disabled = false,
  className = ""
}: SignButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const handleSign = useCallback(async () => {
    if (disabled || status === "signed") return;
    setIsLoading(true);
    try {
      const url = await onSign();
      if (url) {
        setSigningUrl(url);
        // Open in new tab or redirect
        window.open(url, "_blank");
      }
    } finally {
      setIsLoading(false);
    }
  }, [disabled, status, onSign]);

  // Already signed
  if (status === "signed") {
    return <div className={`flex items-center gap-2 text-primary ${className}`}>
        <CheckCircle className="w-5 h-5" />
        <span className="font-medium">Sie haben bereits unterschrieben</span>
      </div>;
  }

  // Declined
  if (status === "declined") {
    return <div className={`flex items-center gap-2 text-red-600 ${className}`}>
        <span className="font-medium">Unterschrift wurde abgelehnt</span>
      </div>;
  }

  // Show URL if available
  if (signingUrl) {
    return <div className={`space-y-3 ${className}`}>
        <a href={signingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors">
          <ExternalLink className="w-4 h-4" />
          Zum Unterschreiben öffnen
        </a>
        <p className="text-sm text-zinc-500">
          Der Link zur Unterschrift wurde in einem neuen Tab geöffnet.
        </p>
      </div>;
  }

  // Ready to sign
  const canSign = status === "sent" || status === "viewed";
  const isPending = status === "pending" || !status;
  return <button onClick={handleSign} disabled={disabled || isLoading || isPending} className={`
        flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors
        ${canSign ? "bg-primary text-white hover:bg-primary/90" : "bg-zinc-200 text-zinc-500 cursor-not-allowed"}
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}>
      {isLoading ? <>
          <Loader2 className="w-5 h-5 animate-spin" />
          Wird geladen...
        </> : isPending ? <>
          <PenTool className="w-5 h-5" />
          Warten auf Vertrag
        </> : <>
          <PenTool className="w-5 h-5" />
          Jetzt unterschreiben
        </>}
    </button>;
}